import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, HStack, VStack, Text, Wrap, WrapItem, Tag, TagLabel, TagCloseButton, Button, Spinner, Alert, AlertIcon, Heading, Icon, Flex } from '@chakra-ui/react';
import { FaHandPointer, FaCheckCircle, FaTimesCircle, FaBriefcase } from 'react-icons/fa';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { GOOGLE_MAPS_LOADER_OPTIONS } from '../../utils/googleMapsLoader';

// Modified ZIP Code Tabulation Areas published by NYC DOHMH.
// Public OpenData endpoint, CORS-enabled, ~1MB.
const NYC_ZIP_GEOJSON_URL =
  process.env.REACT_APP_NYC_ZIP_GEOJSON_URL ||
  'https://data.cityofnewyork.us/api/geospatial/pri4-ifjk?method=export&format=GeoJSON';

const NYC_CENTER = { lat: 40.7128, lng: -74.006 };

// Used only as a fallback if the GeoJSON request fails — keeps the wizard usable.
const FALLBACK_ZIPS_BY_BOROUGH = {
  Manhattan: ['10001','10002','10003','10004','10005','10006','10007','10009','10010','10011','10012','10013','10014','10016','10017','10018','10019','10021','10022','10023','10024','10025','10026','10027','10028','10029','10030','10031','10032','10033','10034','10035','10036','10037','10038','10039','10040','10044','10065','10069','10075','10128','10280','10282'],
  Brooklyn: ['11201','11203','11204','11205','11206','11207','11208','11209','11210','11211','11212','11213','11214','11215','11216','11217','11218','11219','11220','11221','11222','11223','11224','11225','11226','11228','11229','11230','11231','11232','11233','11234','11235','11236','11237','11238','11239','11249'],
  Queens: ['11101','11102','11103','11104','11105','11106','11354','11355','11356','11357','11358','11360','11361','11362','11363','11364','11365','11366','11367','11368','11369','11370','11371','11372','11373','11374','11375','11377','11378','11379','11385','11411','11412','11413','11414','11415','11416','11417','11418','11419','11420','11421','11422','11423','11426','11427','11428','11429','11430','11432','11433','11434','11435','11436','11691','11692','11693','11694','11697'],
  Bronx: ['10451','10452','10453','10454','10455','10456','10457','10458','10459','10460','10461','10462','10463','10464','10465','10466','10467','10468','10469','10470','10471','10472','10473','10474','10475'],
  'Staten Island': ['10301','10302','10303','10304','10305','10306','10307','10308','10309','10310','10311','10312','10314'],
};

function featureZip(feature) {
  return String(
    feature.getProperty('modzcta') ||
    feature.getProperty('MODZCTA') ||
    feature.getProperty('zcta') ||
    feature.getProperty('ZIPCODE') ||
    ''
  );
}

export default function ServiceZipPicker({ value = [], onChange }) {
  const selectedSet = useMemo(() => new Set(value.map(String)), [value]);
  const [geojson, setGeojson] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const mapRef = useRef(null);
  const featureLayerRef = useRef(new Map()); // zip -> google.maps.Data.Feature
  const clickListenerRef = useRef(null);

  // Keep the latest selection + onChange accessible from inside the map's
  // click listener, which is registered once and would otherwise close over
  // stale state (the bug where clicks "replaced" the previous selection).
  const selectedRef = useRef(selectedSet);
  const onChangeRef = useRef(onChange);
  useEffect(() => { selectedRef.current = selectedSet; }, [selectedSet]);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  useEffect(() => {
    let cancelled = false;
    fetch(NYC_ZIP_GEOJSON_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`Failed to load ZIP boundaries (${r.status})`);
        return r.json();
      })
      .then((data) => { if (!cancelled) setGeojson(data); })
      .catch((err) => { if (!cancelled) setLoadError(err.message); });
    return () => { cancelled = true; };
  }, []);

  const toggleZip = (zip) => {
    const z = String(zip);
    const current = selectedRef.current;
    const next = new Set(current);
    if (next.has(z)) next.delete(z); else next.add(z);
    onChangeRef.current(Array.from(next).sort());
  };

  const styleFeature = (feature) => {
    const zip = featureZip(feature);
    const selected = selectedRef.current.has(zip);
    return {
      fillColor: selected ? '#3182CE' : '#A0AEC0',
      fillOpacity: selected ? 0.55 : 0.15,
      strokeColor: selected ? '#2B6CB0' : '#718096',
      strokeWeight: selected ? 1.5 : 0.5,
      clickable: true,
    };
  };

  const onMapLoad = (map) => {
    mapRef.current = map;
    // Set the data-layer style immediately so when features are added later
    // they render with our muted look instead of flashing the default style.
    map.data.setStyle(styleFeature);
  };

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !geojson) return;
    // Reset previous data layer when geojson changes.
    map.data.forEach((f) => map.data.remove(f));
    featureLayerRef.current.clear();
    if (clickListenerRef.current) {
      clickListenerRef.current.remove();
      clickListenerRef.current = null;
    }

    // Set the style BEFORE adding features so we never flash Google's default
    // opaque polygons. setStyle on the data layer is sticky for new features.
    map.data.setStyle(styleFeature);
    map.data.addGeoJson(geojson);
    map.data.forEach((f) => {
      const zip = featureZip(f);
      if (zip) featureLayerRef.current.set(zip, f);
    });
    clickListenerRef.current = map.data.addListener('click', (e) => {
      const zip = featureZip(e.feature);
      if (zip) toggleZip(zip);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geojson, isLoaded]);

  // Restyle when selection changes (reads selectedRef inside styleFeature).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.data.setStyle(styleFeature);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSet]);

  return (
    <VStack align="stretch" spacing={4}>
      {/* Instructions card */}
      <Box bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="lg" p={4}>
        <Heading size="sm" color="blue.800" mb={2}>Pick the NYC areas you can cover</Heading>
        <Text fontSize="sm" color="gray.700" mb={3} lineHeight="1.6">
          The map below shows every NYC ZIP code. Tap or click a ZIP to add it to
          your service area — you only see draws in the ZIPs you choose, so pick
          everywhere you're willing to travel.
        </Text>
        <VStack align="stretch" spacing={2}>
          <HStack align="start" spacing={3}>
            <Flex align="center" justify="center" bg="white" boxSize="28px" borderRadius="full" borderWidth="1px" borderColor="blue.200" flexShrink={0}>
              <Icon as={FaHandPointer} color="blue.500" boxSize={3} />
            </Flex>
            <Text fontSize="sm" color="gray.700"><b>Click a ZIP</b> on the map to select it. The shape turns blue when it's added.</Text>
          </HStack>
          <HStack align="start" spacing={3}>
            <Flex align="center" justify="center" bg="white" boxSize="28px" borderRadius="full" borderWidth="1px" borderColor="blue.200" flexShrink={0}>
              <Icon as={FaCheckCircle} color="green.500" boxSize={3} />
            </Flex>
            <Text fontSize="sm" color="gray.700"><b>Pick as many as you want</b> — the more ZIPs, the more draws you'll see.</Text>
          </HStack>
          <HStack align="start" spacing={3}>
            <Flex align="center" justify="center" bg="white" boxSize="28px" borderRadius="full" borderWidth="1px" borderColor="blue.200" flexShrink={0}>
              <Icon as={FaTimesCircle} color="red.500" boxSize={3} />
            </Flex>
            <Text fontSize="sm" color="gray.700"><b>Click again or hit the × tag</b> below the map to remove a ZIP.</Text>
          </HStack>
          <HStack align="start" spacing={3}>
            <Flex align="center" justify="center" bg="white" boxSize="28px" borderRadius="full" borderWidth="1px" borderColor="blue.200" flexShrink={0}>
              <Icon as={FaBriefcase} color="purple.500" boxSize={3} />
            </Flex>
            <Text fontSize="sm" color="gray.700"><b>After onboarding</b> your dashboard filters jobs to these ZIPs automatically.</Text>
          </HStack>
        </VStack>
      </Box>

      {/* Color legend */}
      <HStack spacing={4} fontSize="xs" color="gray.600" px={1}>
        <HStack spacing={2}>
          <Box w="14px" h="14px" borderRadius="sm" bg="#3182CE" opacity={0.55} borderWidth="1px" borderColor="#2B6CB0" />
          <Text>Selected — you'll see draws here</Text>
        </HStack>
        <HStack spacing={2}>
          <Box w="14px" h="14px" borderRadius="sm" bg="#A0AEC0" opacity={0.2} borderWidth="1px" borderColor="#718096" />
          <Text>Not selected</Text>
        </HStack>
      </HStack>

      {isLoaded ? (
        <Box borderWidth="1px" borderRadius="md" overflow="hidden" h="420px">
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={NYC_CENTER}
            zoom={10}
            onLoad={onMapLoad}
            options={{ mapTypeControl: false, streetViewControl: false, fullscreenControl: false }}
          />
        </Box>
      ) : (
        <Box h="420px" display="flex" alignItems="center" justifyContent="center" borderWidth="1px" borderRadius="md">
          <Spinner />
        </Box>
      )}

      {loadError && (
        <Alert status="warning" fontSize="sm">
          <AlertIcon />
          Couldn't load NYC ZIP boundaries on the map ({loadError}). Use the list below instead.
        </Alert>
      )}

      {(loadError || !geojson) && (
        <Box>
          <Text fontSize="xs" fontWeight="600" color="gray.500" mb={2}>OR PICK FROM LIST</Text>
          <VStack align="stretch" spacing={3}>
            {Object.entries(FALLBACK_ZIPS_BY_BOROUGH).map(([borough, zips]) => (
              <Box key={borough}>
                <Text fontSize="xs" fontWeight="700" color="gray.600" mb={1.5}>{borough}</Text>
                <Wrap spacing={1}>
                  {zips.map((z) => (
                    <WrapItem key={z}>
                      <Button
                        size="xs"
                        variant={selectedSet.has(z) ? 'solid' : 'outline'}
                        colorScheme={selectedSet.has(z) ? 'blue' : 'gray'}
                        onClick={() => toggleZip(z)}
                      >
                        {z}
                      </Button>
                    </WrapItem>
                  ))}
                </Wrap>
              </Box>
            ))}
          </VStack>
        </Box>
      )}

      <Box>
        <HStack justify="space-between" mb={2}>
          <Text fontSize="sm" fontWeight="600">Selected ({value.length})</Text>
          {value.length > 0 && (
            <Button size="xs" variant="ghost" onClick={() => onChange([])}>Clear all</Button>
          )}
        </HStack>
        {value.length === 0 ? (
          <Text fontSize="sm" color="gray.400">No ZIP codes selected yet.</Text>
        ) : (
          <Wrap spacing={1}>
            {value.map((z) => (
              <WrapItem key={z}>
                <Tag size="sm" colorScheme="blue" borderRadius="full">
                  <TagLabel>{z}</TagLabel>
                  <TagCloseButton onClick={() => toggleZip(z)} />
                </Tag>
              </WrapItem>
            ))}
          </Wrap>
        )}
      </Box>
    </VStack>
  );
}
